/* eslint-disable @angular-eslint/no-host-metadata-property */
/* eslint-disable @typescript-eslint/member-ordering */
import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  Optional,
  Self,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NgControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MAT_FORM_FIELD, MatFormField, MatFormFieldControl } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { I18NextModule } from 'angular-i18next';
import { Subject } from 'rxjs';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { getTimeOptions } from 'src/app/common/utils/helpers';
import { BasePlusComponent } from '../base/base-plus.component';

export class SharryTimeInput {
  constructor(
    public hours: string,
    public minutes: string,
  ) {}
}

@Component({
  selector: 'sharry-time-input',
  standalone: true,
  imports: [AsyncPipe, IconComponent, ReactiveFormsModule, FormsModule, MatMenuModule, MatButtonModule, I18NextModule],
  templateUrl: 'time-input.component.html',
  styleUrls: ['time-input.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: TimeInputComponent,
    },
  ],
  host: {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '[class.example-floating]': 'shouldLabelFloat',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '[id]': 'id',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeInputComponent
  extends BasePlusComponent
  implements MatFormFieldControl<SharryTimeInput>, OnDestroy, ControlValueAccessor
{
  static nextId = 0;
  @ViewChild('time', { read: TemplateRef }) time: TemplateRef<HTMLElement>;
  @ViewChild('hours') hoursInput: HTMLInputElement;
  @ViewChild('minutes') minutesInput: HTMLInputElement;
  @Input() text = 'time-input.text';

  parts: UntypedFormGroup;
  stateChanges: Subject<void> = new Subject<void>();
  focused = false;
  touched = false;
  id = `sharry-time-input-${TimeInputComponent.nextId++}`;
  controlType = 'sharry-time-input';
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-describedby') userAriaDescribedBy: string;
  @Input() isCreate: boolean;
  selectedHour: string;
  selectedMinute: string;
  dialogRef: MatDialogRef<HTMLElement>;

  constructor(
    fb: UntypedFormBuilder,
    @Optional() @Inject(MAT_FORM_FIELD) public parentFormField: MatFormField,
    @Optional() @Self() public ngControl: NgControl,
    private _elementRef: ElementRef<HTMLElement>,
    private _focusMonitor: FocusMonitor,
    private dialog: MatDialog,
  ) {
    super();
    this.parts = fb.group({
      hours: new UntypedFormControl('', [Validators.required, Validators.min(0), Validators.max(23)]),
      minutes: new UntypedFormControl('', [Validators.required, Validators.min(0), Validators.max(45)]),
    });
    if (this.ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }
  }

  get empty() {
    const {
      value: { hours, minutes },
    } = this.parts;
    return !hours && !minutes;
  }

  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  private _placeholder: string;

  @Input()
  get placeholder() {
    return this._placeholder;
  }

  set placeholder(plh: string) {
    this._placeholder = plh;
    this.stateChanges.next();
  }

  private _required = false;

  @Input()
  get required() {
    return this._required;
  }

  set required(req: boolean) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  private _disabled = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    if (this._disabled) {
      this.parts.disable();
    } else {
      this.parts.enable();
      this.onChange(this.value);
    }
    this.stateChanges.next();
  }

  @Input()
  get value(): SharryTimeInput | null {
    const {
      value: { hours, minutes },
    } = this.parts;
    return new SharryTimeInput(hours, minutes);
  }

  set value(time: SharryTimeInput | null) {
    time = time || new SharryTimeInput('', '');
    this.parts.setValue(time);
    this.stateChanges.next();
  }

  get errorState(): boolean {
    return this.parts.invalid && this.touched;
  }

  private _timeOptions: Map<string, string[]> = getTimeOptions();

  @Input()
  get timeOptions(): Map<string, string[]> {
    return this._timeOptions;
  }

  set timeOptions(value: Map<string, string[]> | null) {
    this._timeOptions = value || getTimeOptions();
  }

  private _minTime: { hours: number; minutes: number } = { hours: 0, minutes: 0 };

  @Input()
  get minTime(): { hours: number; minutes: number } {
    return this._minTime;
  }

  set minTime(value: SharryTimeInput | { hours: number; minutes: number } | null) {
    const hours: number = !!value && !!value.hours ? Number(value.hours) : 0;
    const minutes: number = !!value && !!value.minutes ? Number(value.minutes) : 0;
    this._minTime = { hours, minutes };
  }

  onChange: any = (_: any) => {};

  onTouched: any = () => {};

  onFocusIn() {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(event: FocusEvent) {
    if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      const value = { ...this.value };
      if (Number(this.value.hours) > 23) {
        value.hours = '23';
      }
      if (Number(this.value.minutes) > 45) {
        value.minutes = '45';
      }
      this.formatNumber('hours', value.hours);
      this.formatNumber('minutes', value.minutes);
      this.parts.setValue(this.value);
      this.onChange(this.value);
      this.stateChanges.next();
    }
  }

  autoFocusPrev(control: AbstractControl, prevElement: HTMLInputElement): void {
    if ((control.value || '').length < 1) {
      this._focusMonitor.focusVia(prevElement, 'program');
    }
  }

  setDescribedByIds(ids: string[]) {
    const controlElement = this._elementRef.nativeElement.querySelector('.sharry-time-input-container');
    if (controlElement) {
      controlElement.setAttribute('aria-describedby', ids.join(' '));
    }
  }

  onContainerClick() {
    this.onFocusIn();
  }

  writeValue(value: SharryTimeInput | null): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  formatNumber(formField: 'minutes' | 'hours', value: string) {
    let forcedValue: SharryTimeInput;
    if (!isNaN(Number(value))) {
      if (formField === 'minutes') {
        forcedValue = {
          minutes: (Math.ceil(Number(value) / 15) * 15).toLocaleString(undefined, { minimumIntegerDigits: 2 }),
          hours: this.parts.get('hours').value,
        };
      } else {
        forcedValue = {
          minutes: this.parts.get('minutes').value,
          hours: Number(value).toLocaleString(undefined, { minimumIntegerDigits: 2 }),
        };
      }
      if (Number(forcedValue.hours) < this._minTime.hours) {
        forcedValue.hours = this._minTime.hours.toLocaleString(undefined, { minimumIntegerDigits: 2 });
      } else if (
        Number(forcedValue.hours) === this._minTime.hours &&
        Number(forcedValue.minutes) < this._minTime.minutes
      ) {
        forcedValue.minutes = this._minTime.minutes.toLocaleString(undefined, { minimumIntegerDigits: 2 });
      }
      this.writeValue(forcedValue);
    }
  }

  onOpenTime(hours: string, minutes: string) {
    this.selectedHour = hours || '00';
    this.selectedMinute = minutes || '00';
    this.dialogRef = this.dialog.open(this.time, {
      width: '300px',
      maxWidth: '300px',
      minWidth: '300px',
      autoFocus: false,
      restoreFocus: false,
      maxHeight: '500px',
    });
    this.subscriptions.push(
      this.dialogRef.afterClosed().subscribe({
        next: res => {
          if (res) {
            this.writeValue({ hours: this.selectedHour, minutes: this.selectedMinute });
            this.onChange(this.value);
          }
        },
      }),
    );
  }

  onConfirm() {
    this.dialogRef.close(true);
  }

  onSelectedHourChange(direction: 'up' | 'down') {
    let switchIndex: number;
    const hoursList = Array.from(this._timeOptions.keys());
    const currentIndex = hoursList.indexOf(this.selectedHour);
    if (currentIndex === 0 && direction === 'down') {
      switchIndex = hoursList.length - 1;
    } else if (currentIndex === hoursList.length - 1 && direction === 'up') {
      switchIndex = 0;
    } else {
      if (direction === 'down') {
        switchIndex = currentIndex - 1;
      } else {
        switchIndex = currentIndex + 1;
      }
    }
    this.selectedHour = hoursList[switchIndex];
    if (this._timeOptions.get(this.selectedHour).indexOf(this.selectedMinute) === -1) {
      this.selectedMinute = this._timeOptions.get(this.selectedHour)[0];
    }
  }

  onSelectedMinuteChange(direction: 'up' | 'down') {
    let switchIndex: number;
    const minutesList = this._timeOptions.get(this.selectedHour);
    const currentIndex = minutesList.indexOf(this.selectedMinute);
    if (currentIndex === 0 && direction === 'down') {
      switchIndex = minutesList.length - 1;
    } else if (currentIndex === minutesList.length - 1 && direction === 'up') {
      switchIndex = 0;
    } else {
      if (direction === 'down') {
        switchIndex = currentIndex - 1;
      } else {
        switchIndex = currentIndex + 1;
      }
    }
    this.selectedMinute = minutesList[switchIndex];
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this._focusMonitor.stopMonitoring(this._elementRef);
  }
}
